/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2025. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import { defineStore } from 'pinia';
import registryApiService from '../../../services/api/registry.api-service';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';

const useRegistryStore = defineStore('registry', {
  state: () => ({
    images: [],
    lastRepository: '',
    listOfTags: [],
    selectedImage: '',
  }),

  actions: {
    async fetchImage(data) {
      const params = {
        limit: data.limit,
      };

      if (this.lastRepository) {
        params.lastImage = this.lastRepository;
      }

      const response = await registryApiService.getImagesFromRegistry(params);
      if (response.images && response.images.length > 0) {
        const lastRepository = response.images[response.images.length - 1];
        const images = response.images.map((image) => image.replace(/^registry\//, ''));
        this.images.push(...images);
        this.lastRepository = lastRepository;
      }
    },

    async fetchTagsOfSelectedImage(data) {
      try {
        const { image, ignoreInterceptor, usedByReleasedVersion } = data;
        const response = await registryApiService.getImageTags({
          image,
          ignoreInterceptor,
          usedByReleasedVersion,
        });
        const listOfTags = response.tags.map((i) => ({
          name: i.tag,
          digest: i.digest,
          usedByReleasedVersion: i.usedByReleasedVersion,
        }));
        this.selectedImage = image.replace(/^registry\//, '');
        this.listOfTags = listOfTags;
      } catch {
        this.listOfTags = [];
      }
    },

    async deleteTag(data) {
      const utilsStore = useUtilsStore();
      await registryApiService.deleteTag(data);
      const digestToDelete = this.listOfTags.find((tag) => tag.name === data.tag)?.digest;

      if (!digestToDelete) {
        return;
      }

      const updatedTags = this.listOfTags.filter((tag) => tag.digest !== digestToDelete);
      this.listOfTags = updatedTags;
      utilsStore.showCustomToast({
        text: 'managementSystemRegistry.successfullyDeleted',
        color: 'success',
        showClose: true,
      });
    },

    updateListOfTags({ name, digest, usedByReleasedVersion, action }) {
      // Check if the tag already exists
      if (action === 'push' && !this.listOfTags.find((tag) => tag.name === name)) {
        this.listOfTags.push({ name, digest, usedByReleasedVersion });
        return;
      }
      if (action === 'delete') {
        this.listOfTags = this.listOfTags.filter((tag) => tag.name !== name);
      }
    },

    updateListOfImages({ imageName, action }) {
      if (action === 'push' && !this.images.includes(imageName)) {
        // Find the correct position to insert the string
        const insertIndex = this.images.findIndex((existingString) => imageName < existingString);
        if (insertIndex === -1) {
          // If no smaller string is found, append to the end
          this.images.push(imageName);
          this.lastRepository = imageName;
        } else {
          // Insert at the found index
          this.images.splice(insertIndex, 0, imageName);
        }
      }
    },

    updateListOfImagesAndTags({ image, digest, usedByReleasedVersion, action }) {
      const imageName = image.match(/(?<=registry\/)[^:]+/)[0];
      const tag = image.split(':').reverse()[0];
      if (this.images?.includes(imageName) && this.selectedImage === imageName) {
        this.updateListOfTags({ name: tag, digest, usedByReleasedVersion, action });
        return;
      }
      this.updateListOfImages({ imageName, action });
    },

    /**
     * Handling incoming mqtt events coming from backend about changes in registry.
     * Based on event type, it updates list of images and tags in registry store.
     * @param {{name: string, params: object}} param0 - mqtt message payload
     * @returns void
     */
    handleDockerRegistryEvents({ name, params }) {
      const actionMap = {
        image_pushed_to_registry: 'push',
        image_deleted_from_registry: 'delete',
      };

      const action = actionMap[name];
      if (!action) return;

      this.updateListOfImagesAndTags({
        image: params.imageName,
        digest: params.digest,
        ...(action === 'push' && { usedByReleasedVersion: params.usedByReleasedVersion }),
        action,
      });
    },
  },
});

export default useRegistryStore;
